// App-Rahmen (design-system §3.1): Sidebar auf `canvas`, daneben die Arbeitsfläche als weiße Karte mit Topbar,
// Verbindungsleiste und Inhalt – nur der Inhalt scrollt. Rechts dockt der KI-Assistent an. Unter 768 px:
// Kopfzeile, Inhalt, untere Leiste. 768–1023 px: Schiene, die volle Seitenleiste legt sich auf Wunsch darüber
// (NavOverlay). Hier hängen auch die globalen Tastenkürzel, der Seitentitel und der Fokus nach einem Seitenwechsel.
// Sichere Bereiche (Statusleiste, Home-Indikator, Kamera) hält der Rahmen frei – in der App liegt die WebView unter
// den Systemleisten (docs/mobile/SPEZIFIKATION.md §4.7); im Browser sind sie 0.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useMatches, useNavigate } from 'react-router'
import { useWebModules } from '../api/context'
import { setRouteTitle } from '../api/pages/title'
import { useSession } from '../api/session'
import { cn } from '../lib/cn'
import { useHotkey } from '../lib/hotkeys'
import { useIsPhone, useMediaQuery } from '../lib/useMediaQuery'
import { useConnectionStore, useIsOffline } from '../stores/connection'
import { shell, useShellStore } from '../stores/shell'
import { Banner } from '../ui/Banner'
import { Kbd } from '../ui/Kbd'
import { Modal } from '../ui/Modal'
import { AssistantDock } from './AssistantDock'
import { BottomBar, MobileHeader, MobileNavSheet } from './MobileChrome'
import { breadcrumbsFor, buildNavigation } from './nav'
import { NavOverlay } from './NavOverlay'
import { NotificationCenter } from './NotificationCenter'
import { ShellPalette } from './Palette'
import { ScanDialog } from './ScanDialog'
import { SecurityBanner } from './SecurityBanner'
import { Sidebar } from './Sidebar'
import { SupportBanner } from './SupportBanner'
import { Topbar } from './Topbar'

interface RouteHandle {
  title?: string
}

const SHORTCUTS: { keys: string; label: string }[] = [
  { keys: 'mod+k', label: 'Suchen und Befehle' },
  { keys: 'mod+j', label: 'KI-Assistent öffnen oder schließen' },
  { keys: 'mod+b', label: 'Seitenleiste ein- oder ausklappen' },
  { keys: 'o', label: 'Standort wechseln' },
  { keys: 'g s', label: 'Zur Startseite' },
  { keys: 'f', label: 'Suchfeld der Liste' },
  { keys: 'mod+s', label: 'Speichern (in Formularen)' },
  { keys: 'esc', label: 'Oberste Ebene schließen' },
  { keys: '?', label: 'Diese Übersicht' },
]

function ShortcutsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Tastenkürzel" size="sm">
      <dl className="divide-y divide-line-subtle text-dense">
        {SHORTCUTS.map((entry) => (
          <div key={entry.keys} className="flex items-center justify-between gap-4 py-2">
            <dt className="text-fg">{entry.label}</dt>
            <dd>
              <Kbd hotkey={entry.keys} />
            </dd>
          </div>
        ))}
      </dl>
    </Modal>
  )
}

/** Leiste unter der Topbar, solange Server oder Netz nicht erreichbar sind (design-system §6.5). */
function OfflineBanner() {
  const offline = useIsOffline()
  if (!offline) return null
  return (
    <Banner tone="warning" flush title="Keine Verbindung" className="shrink-0">
      Angezeigte Daten können veraltet sein. Änderungen sind erst wieder möglich, wenn die Verbindung steht.
    </Banner>
  )
}

export function AppShell() {
  const modules = useWebModules()
  const session = useSession()
  const location = useLocation()
  const navigate = useNavigate()
  const matches = useMatches()
  const phone = useIsPhone()
  // 768–1023 px: Sidebar als Schiene; ab 1280 px dockt der Assistent an, darunter überlagert er
  const tablet = useMediaQuery('(max-width: 1023.98px)')
  const wide = useMediaQuery('(min-width: 1280px)', true)
  const roomy = useMediaQuery('(min-width: 1440px)', true)
  const userCollapsed = useShellStore((state) => state.sidebarCollapsed)
  const assistantOpen = useShellStore((state) => state.assistantOpen)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  // Zustand des Echtzeit-Kanals am Rahmen ablesbar (Fehlersuche, E2E): idle | connecting | online | offline | unavailable
  const realtime = useConnectionStore((state) => state.realtime)

  const navigation = useMemo(() => buildNavigation(modules, session), [modules, session])
  const title = [...matches].reverse().find((match) => (match.handle as RouteHandle | undefined)?.title)?.handle as
    RouteHandle | undefined
  const crumbs = useMemo(
    () => breadcrumbsFor(navigation, location.pathname, title?.title),
    [navigation, location.pathname, title?.title],
  )
  // zwischen 1280 und 1440 px macht die Sidebar dem angedockten Assistenten Platz
  const collapsed = tablet || userCollapsed || (assistantOpen && wide && !roomy)

  // ---- Seitentitel, Fokus und Ansage nach einem Seitenwechsel ----
  // eingeklappte Gruppen der Navigation gelten je Person
  const personId = session?.user.id ?? null
  useEffect(() => shell.setPerson(personId), [personId])
  useEffect(() => {
    setRouteTitle(title?.title ?? null)
  }, [title?.title])
  const mainRef = useRef<HTMLElement | null>(null)
  const firstRender = useRef(true)
  const [announcement, setAnnouncement] = useState('')
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    shell.setMobileNav(false)
    const main = mainRef.current
    if (main) {
      main.scrollTop = 0
      // Fokus an den Anfang des Inhalts: Screenreader lesen die neue Seite, Tab beginnt dort
      main.focus({ preventScroll: true })
    }
    setAnnouncement(title?.title ?? '')
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps -- nur der Pfad löst aus

  // ---- globale Tastenkürzel (design-system §6.1) ----
  useHotkey('mod+k', () => shell.openPalette(), { allowInInputs: true })
  useHotkey('mod+j', () => shell.toggleAssistant(), { allowInInputs: true })
  // Tablet: ⌘B öffnet bzw. schließt die überlagernde Seitenleiste; Desktop: ein- und ausklappen
  useHotkey(
    'mod+b',
    () => (tablet ? shell.setMobileNav(!useShellStore.getState().mobileNavOpen) : shell.toggleSidebar()),
    { allowInInputs: true, enabled: !phone },
  )
  // Breite wechselt auf Desktop (Drehen, Fenster): eine offene Navigation als Ebene gibt es dort nicht
  useEffect(() => {
    if (!phone && !tablet) shell.setMobileNav(false)
  }, [phone, tablet])
  useHotkey('g s', () => void navigate('/'))
  useHotkey('?', () => setShortcutsOpen(true))

  const content = (
    <>
      <OfflineBanner />
      {/* Support-Zugang des Betreibers: deutlich, nicht ausblendbar, mit Weg zurück (docs/anmeldung/SPEZIFIKATION.md §18) */}
      <SupportBanner />
      {/* Zwei-Faktor-Frist, wenige Wiederherstellungscodes, Fehlversuche seit der letzten Anmeldung (Sprung ins Profil) */}
      <SecurityBanner />
      {/* eigener Bezugsrahmen: fest stehende Leisten einer Seite (Hauptaktion auf dem Telefon) bleiben über der unteren Leiste */}
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col [contain:layout]">
        <main
          ref={mainRef}
          id="inhalt"
          tabIndex={-1}
          data-testid="main"
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto outline-none"
        >
          <Outlet />
        </main>
      </div>
    </>
  )

  const overlays = (
    <>
      <ShellPalette navigation={navigation} />
      <ScanDialog />
      <NotificationCenter />
      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
    </>
  )

  const skipLink = (
    <a
      href="#inhalt"
      className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-(--z-toast) focus:rounded-sm focus:bg-inverse focus:px-3 focus:py-2 focus:text-dense focus:text-fg-inverse"
      onClick={(event) => {
        event.preventDefault()
        mainRef.current?.focus()
      }}
    >
      Zum Inhalt
    </a>
  )

  // Ein Baum für alle Breiten: Beim Drehen oder im Split View wechselt nur der Rahmen (Kopfzeile/untere Leiste bzw.
  // Seitenleiste/Topbar). Der Inhalt bleibt an derselben Stelle im Baum – React behält die Seite mit Eingaben, offenen
  // Dialogen und Scrollstand (docs/mobile/SPEZIFIKATION.md §6.4).
  return (
    <div
      className={
        phone
          ? 'flex h-dvh flex-col bg-surface text-fg'
          : 'flex h-dvh bg-canvas pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] text-fg'
      }
      data-testid="app-shell"
      data-layout={phone ? 'phone' : tablet ? 'tablet' : 'desktop'}
      data-realtime={realtime}
    >
      {skipLink}
      {phone ? (
        <MobileHeader />
      ) : (
        <Sidebar navigation={navigation} collapsed={collapsed} onShowShortcuts={() => setShortcutsOpen(true)} />
      )}
      <div
        className={
          phone
            ? 'flex min-h-0 min-w-0 flex-1'
            : cn(
                'my-2 mr-2 flex min-w-0 flex-1 overflow-hidden rounded-lg border border-line bg-surface',
                'print:m-0 print:rounded-none print:border-0',
              )
        }
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {phone ? null : (
            <Topbar crumbs={crumbs} sidebarCollapsed={collapsed} sidebarToggle={tablet ? 'overlay' : 'collapse'} />
          )}
          {content}
        </div>
        <AssistantDock docked={!phone && wide} />
      </div>
      {phone ? <BottomBar navigation={navigation} /> : null}
      {phone ? <MobileNavSheet navigation={navigation} /> : null}
      {tablet && !phone ? <NavOverlay navigation={navigation} onShowShortcuts={() => setShortcutsOpen(true)} /> : null}
      {overlays}
    </div>
  )
}
